import { Key, AlertTriangle, MapPin } from 'lucide-react';
import type { TemplateEditorState } from '../templateEditor/types';

export interface MatchKeyTabContentProps {
  state: TemplateEditorState;
  onChange: (patch: Partial<TemplateEditorState>) => void;
  errors?: string[];
}

export default function MatchKeyTabContent({ state, onChange, errors = [] }: MatchKeyTabContentProps) {
  const matchKeyMappings = state.fieldMappings.filter(m => m.isMatchKey);
  const needsMatchKey = state.importBehavior === 'upsert' || state.importBehavior === 'full-sync';
  const isShell = state.mode === 'shell';

  return (
    <div className="space-y-5 max-w-xl">
      {errors.length > 0 && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 space-y-1">
          {errors.map(e => (
            <p key={e} className="text-xs text-red-700">• {e}</p>
          ))}
        </div>
      )}

      {isShell ? (
        // Shell mode: there are no mappings yet, so the author declares the
        // *target field name* they will match on at import time. The import
        // wizard enforces that the uploaded file's chosen source column is
        // mapped to this target.
        <div className="space-y-3">
          <p className="text-xs text-gray-500">
            Shell templates defer column mapping to import time. Declare here which
            <strong> target field</strong> should be used to identify existing records
            (for upsert / full-sync). The import wizard will ensure a source column
            is mapped to this field before running.
          </p>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Match Key Target Field {needsMatchKey && <span className="text-red-500">*</span>}
            </label>
            <p className="text-[11px] text-gray-400 mb-2">
              e.g. <code>parcelNumber</code>, <code>code</code>, <code>name</code>. This is the destination field name, not a column from any specific file.
            </p>
            <input
              type="text"
              data-testid="shell-match-key-input"
              className="w-full max-w-xs border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 focus:outline-hidden focus:ring-1 focus:ring-blue-500"
              placeholder={
                state.dataMode === 'parcel' ? 'parcelNumber' :
                state.dataMode === 'lookup' ? 'code' :
                state.dataMode === 'contact' ? 'name' :
                'e.g. externalId'
              }
              value={state.matchKeyField}
              onChange={e => onChange({ matchKeyField: e.target.value.trim() })}
            />
          </div>
          {!needsMatchKey && (
            <p className="text-[11px] text-gray-500">
              Current behavior is <strong>{state.importBehavior}</strong>, which does not require a match key.
              You can still declare one for documentation.
            </p>
          )}
        </div>
      ) : (
      <p className="text-xs text-gray-500">
        Match keys identify existing records during upsert and full-sync operations.
        Mark fields as match keys in the <strong>Field Mappings</strong> tab, then select the primary key here.
      </p>
      )}

      {/* Bound-mode only: mapping-derived match-key status and dropdown */}
      {!isShell && (matchKeyMappings.length === 0 ? (
        <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-4">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>
            No match keys defined. Go to the <strong>Field Mappings</strong> tab and check "Match Key" for at least one field.
          </span>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="text-xs font-semibold text-gray-700">Fields marked as match keys:</div>
          <div className="flex flex-wrap gap-2">
            {matchKeyMappings.map(m => (
              <span
                key={m.targetField}
                className="inline-flex items-center gap-1.5 bg-blue-50 border border-blue-200 text-blue-800 text-xs rounded-full px-3 py-1"
              >
                <Key className="h-3 w-3" />
                {m.targetField} ← {m.sourceColumns.join(', ')}
              </span>
            ))}
          </div>
        </div>
      ))}

      {!isShell && matchKeyMappings.length > 0 && (
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">
            Primary Match Key Field {needsMatchKey && <span className="text-red-500">*</span>}
          </label>
          <p className="text-[11px] text-gray-400 mb-2">
            The field used to pair incoming rows with existing records.
          </p>
          <select
            data-testid="match-key-select"
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 focus:outline-hidden focus:ring-1 focus:ring-blue-500 max-w-xs"
            value={state.matchKeyField}
            onChange={e => onChange({ matchKeyField: e.target.value })}
          >
            <option value="">— Select match key field —</option>
            {matchKeyMappings.map(m => (
              <option key={m.targetField} value={m.targetField}>
                {m.targetField} ← {m.sourceColumns.join(', ')}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="border border-gray-200 rounded-lg p-3 bg-gray-50 text-xs text-gray-600 space-y-1.5">
        <div className="font-semibold text-gray-700 mb-1">Import behavior context</div>
        <div><strong>Upsert:</strong> if a record with a matching key exists, it is updated; otherwise a new record is created.</div>
        <div><strong>Full Sync:</strong> same as Upsert, plus records absent from the file are soft-deleted.</div>
        <div><strong>Insert Only:</strong> rows with an existing match key are skipped entirely.</div>
        <div><strong>Append:</strong> always inserts; duplicates are allowed.</div>
      </div>

      {/* Jurisdiction boundary filter — lives here (not under Geocoding)
          because it's a matching/dedup behavior, not an address-resolution
          concern. Silently ignored for lookup-mode templates. */}
      {state.dataMode !== 'lookup' && (
        <div className="border border-gray-200 rounded-lg p-4 space-y-3">
          <div className="flex items-center gap-2">
            <MapPin className="h-3.5 w-3.5 text-gray-500" />
            <div className="text-xs font-semibold text-gray-700">Jurisdiction Boundary Filter</div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              data-testid="boundary-check-toggle"
              className="w-4 h-4"
              checked={!!state.boundaryCheckEnabled}
              onChange={e => onChange({ boundaryCheckEnabled: e.target.checked })}
            />
            <span className="text-sm text-gray-700">Skip records outside the department&apos;s jurisdiction boundary</span>
          </label>
          {state.boundaryCheckEnabled ? (
            <p className="text-xs text-gray-500">
              During dry run and commit, any record whose geometry or coordinates fall outside the
              department&apos;s configured boundary polygon will be counted as <strong>Out of Bounds</strong>
              and excluded from the import. Requires a district boundary to be configured for the department.
            </p>
          ) : (
            <p className="text-xs text-amber-700">
              <strong>Warning:</strong> all records in the file will be imported regardless of location.
              Turn this on to exclude parcels outside your jurisdiction.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
